"use client";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
// import toast from "react-hot-toast";
import i18n from "../../i18n/i18n";
import { toast } from "react-toastify";
import { storeApiResponse, getApiResponse } from "../../utils/offlineStorage";
import { queueRequest, getPendingCount, isQueueableRequest } from "../../utils/offlineQueue";
import { setPendingCount } from "../Slices/sync.slice";

// Detects backend `message` that's actually an i18next key
// (`namespace::path.to.key` or `domain.action.result`) and tries to translate
// it. Falls back to the raw key when no translation exists, so a missing
// bundle key shows up visibly instead of crashing or rendering "undefined".
const I18N_KEY_PATTERN =
  /^[A-Za-z][A-Za-z0-9_]*(::[A-Za-z][A-Za-z0-9_.]*|\.[A-Za-z][A-Za-z0-9_.]*)+$/;

// Map of plain-English backend messages → i18n keys. Used because some
// endpoints (notably POS) return human prose like "Order created successfully."
// rather than an i18n key. Until the backend switches to keys everywhere, this
// table lets us surface translated copy. Keys live under `backendMessages.*`
// in the global namespace.
const BACKEND_MESSAGE_TO_KEY = {
  "Order created successfully.": "backendMessages.orderCreated",
  "Order updated successfully.": "backendMessages.orderUpdated",
  "Order cancelled successfully.": "backendMessages.orderCancelled",
  "Preparing started. Ingredients deducted.": "backendMessages.preparingStarted",
  // Verified against Apidog screenshots 2026-05-18: the backend sends
  // "Order is ready." for mark-ready and "Order served." for mark-served,
  // not "Mark ready/served successful." that earlier guesses assumed.
  "Order is ready.": "backendMessages.markReady",
  "Order served.": "backendMessages.markServed",
};

// Detects raw database/stack-trace leaks the backend occasionally surfaces
// (e.g. POST /pos/terminals returning the full `SQLSTATE[23000] Duplicate
// entry ...` exception — see daily-report 2026-05-25 + 2026-06-01). Showing
// that to a cashier is a UX/security smell, so we replace it with a clean
// generic message. Exported so inline error UIs can apply the same filter
// before setting their own state.
const RAW_DB_LEAK_PATTERN =
  /SQLSTATE|Integrity constraint|Duplicate entry|Connection:\s*mysql|^SQL:\s|insert into `\w+`|select .* from `\w+`/i;
export const sanitizeBackendErrorMessage = (message) => {
  if (typeof message !== "string" || !message) return message;
  if (!RAW_DB_LEAK_PATTERN.test(message)) return message;
  // i18n.language is "ar"/"en" — keep this in sync with the rest of the app.
  const isArabic = (i18n.language || "ar").toLowerCase().startsWith("ar");
  return isArabic
    ? "حدث خطأ في الخادم. تواصل مع المسؤول."
    : "A server error occurred. Please contact your administrator.";
};

const translateBackendMessage = (message) => {
  if (typeof message !== "string" || !message) return message;

  // -1) Strip raw DB/stack-trace leaks BEFORE any other processing so a
  //     SQLSTATE blob never reaches a toast or modal verbatim.
  const sanitized = sanitizeBackendErrorMessage(message);
  if (sanitized !== message) return sanitized;

  // 0) Exact-string match against known backend prose (POS messages etc.).
  //    Runs FIRST — before the "has spaces" short-circuit below — because
  //    the natural messages all contain spaces and would otherwise pass
  //    through untranslated.
  const proseKey = BACKEND_MESSAGE_TO_KEY[message];
  if (proseKey) {
    const fromMap = i18n.t(proseKey, { defaultValue: "" });
    if (fromMap && fromMap !== proseKey) return fromMap;
  }

  if (message.includes(" ") || message.includes("://")) return message;
  if (!I18N_KEY_PATTERN.test(message)) return message;

  // 1) Try the key as-is — i18next handles the `::` namespace separator natively.
  const direct = i18n.t(message, { defaultValue: "" });
  if (direct && direct !== message) return direct;

  // 2) Fall back to the dotted portion (after `::`) in the default `global` ns,
  //    so future translations can be added there without touching this file.
  const sepIdx = message.indexOf("::");
  if (sepIdx !== -1) {
    const stripped = message.slice(sepIdx + 2);
    const viaGlobal = i18n.t(stripped, { defaultValue: "" });
    if (viaGlobal && viaGlobal !== stripped) return viaGlobal;
  }

  // 3) No translation found — surface the raw key.
  return message;
};

// Helper function to show backend errors
function showBackendErrors(backendError, fallbackMessage) {
  // console.log("[showBackendErrors] Called with:", { backendError, fallbackMessage });

  if (backendError && typeof backendError === "object") {
    // Handle object with error messages (e.g., {code: ["قيمة الكود مُستخدمة من قبل"]})
    let hasShownMessage = false;

    // First, try to show messages from errors object
    Object.values(backendError).forEach(messages => {
      if (Array.isArray(messages)) {
        messages.forEach(msg => {
          if (msg && typeof msg === "string" && msg.trim()) {
            // console.log("[showBackendErrors] Showing error from array:", msg);
            toast.error(translateBackendMessage(msg), {
              autoClose: 5000,
              position: "top-right",
            });
            hasShownMessage = true;
          }
        });
      } else if (messages && typeof messages === "string" && messages.trim()) {
        // console.log("[showBackendErrors] Showing error from string:", messages);
        toast.error(translateBackendMessage(messages), {
          autoClose: 5000,
          position: "top-right",
        });
        hasShownMessage = true;
      }
    });

    // If no messages were shown from errors object, show fallback
    if (!hasShownMessage) {
      if (
        fallbackMessage &&
        typeof fallbackMessage === "string" &&
        fallbackMessage.trim()
      ) {
        // console.log("[showBackendErrors] Showing fallback message:", fallbackMessage);
        toast.error(translateBackendMessage(fallbackMessage), {
          autoClose: 5000,
          position: "top-right",
        });
      } else {
        // Last resort: show first error value if available
        const firstErrorValue = Object.values(backendError)[0];
        if (
          Array.isArray(firstErrorValue) &&
          firstErrorValue.length > 0 &&
          typeof firstErrorValue[0] === "string"
        ) {
          // console.log("[showBackendErrors] Showing first error value:", firstErrorValue[0]);
          toast.error(translateBackendMessage(firstErrorValue[0]), {
            autoClose: 5000,
            position: "top-right",
          });
        } else if (
          typeof firstErrorValue === "string" &&
          firstErrorValue.trim()
        ) {
          // console.log("[showBackendErrors] Showing first error value as string:", firstErrorValue);
          toast.error(translateBackendMessage(firstErrorValue), {
            autoClose: 5000,
            position: "top-right",
          });
        }
      }
    }
  } else if (
    backendError &&
    typeof backendError === "string" &&
    backendError.trim()
  ) {
    // console.log("[showBackendErrors] Showing backendError as string:", backendError);
    toast.error(translateBackendMessage(backendError), {
      autoClose: 5000,
      position: "top-right",
    });
  } else if (
    fallbackMessage &&
    typeof fallbackMessage === "string" &&
    fallbackMessage.trim()
  ) {
    // console.log("[showBackendErrors] Showing fallback message only:", fallbackMessage);
    toast.error(translateBackendMessage(fallbackMessage), {
      autoClose: 5000,
      position: "top-right",
    });
  } else {
    console.warn("[showBackendErrors] No valid error message to show");
  }
}

export const apiRequest = createAsyncThunk(
  "api/request",
  async (
    {
      entity,
      url,
      method = "GET",
      data = null,
      body = null,
      headers = {},
      showErrorToast = null,
      showSuccessToast = null,
      skipAutoLogout = false,
    },
    { rejectWithValue, dispatch },
  ) => {
    const token = localStorage.getItem("token");
    // Support both 'data' and 'body' parameters (body is alias for data)
    const requestData = data !== null ? data : body;
    const fullUrl = `${process.env.REACT_APP_BASE_URL}/${url}`;

    try {
      const isOffline = !navigator.onLine;

      // For GET requests, try offline cache first when offline
      if (method === "GET" && isOffline) {
        const cachedData = await getApiResponse(fullUrl);
        if (cachedData) {
          return { entity, data: cachedData, success: true, fromCache: true };
        }
      }

      // For POST/PUT/PATCH/DELETE when offline: queue and return success.
      // Skip queueing for non-queueable requests (FormData bodies, auth/payment
      // endpoints) — those fall through to the live axios call below, which
      // fails offline and surfaces the real error instead of a fake success.
      if (
        isOffline &&
        ["POST", "PUT", "PATCH", "DELETE"].includes(method) &&
        isQueueableRequest({ url, data: requestData })
      ) {
        try {
          await queueRequest({
            entity,
            url,
            fullUrl,
            method,
            data: requestData,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              Accept: "application/json",
              "Accept-Language": i18n.language || "ar",
              ...headers,
            },
          });
          const queuedMsg =
            i18n.language === "ar"
              ? "تم حفظ البيانات محلياً وسيتم مزامنتها عند الاتصال"
              : "Data saved locally and will sync when online";
          // Offline-related toasts hidden per request
          // if (showSuccessToast !== false) { toast.info(queuedMsg, { autoClose: 4000, position: 'top-right' }); }
          const count = await getPendingCount();
          if (dispatch) dispatch(setPendingCount(count));
          return {
            entity,
            data: { message: queuedMsg, queued: true, success: true },
            success: true,
            fromQueue: true,
          };
        } catch (queueErr) {
          console.error("[API Request] Failed to queue:", queueErr);
          return rejectWithValue({
            entity,
            error:
              i18n.language === "ar"
                ? "فشل حفظ البيانات محلياً"
                : "Failed to save locally",
            success: false,
          });
        }
      }

      const response = await axios({
        url: fullUrl,
        method,
        data: requestData,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
          "Accept-Language": i18n.language || "ar",
          ...headers,
        },
      });

      // Success
      if ([200, 201].includes(response.status)) {
        // Store GET responses in offline cache
        if (method === "GET" && response.data) {
          storeApiResponse(fullUrl, response.data, method, entity).catch(
            err => {
              console.warn("[API Request] Failed to cache response:", err);
            },
          );
        }

        // Show success toast based on showSuccessToast parameter or default behavior
        const shouldShowSuccessToast =
          showSuccessToast === true ||
          (showSuccessToast === null &&
            ["POST", "PUT", "PATCH", "DELETE"].includes(method));

        if (shouldShowSuccessToast) {
          // Show success message from API. The backend may return an i18next
          // key (e.g. `tenant::tenant.login_successful`) — translate it before
          // toasting so users see human-readable text.
          const successMessage =
            translateBackendMessage(response.data?.message) ||
            (i18n.language === "ar" ? "تم بنجاح" : "Success");

          toast.success(successMessage, {
            autoClose: 3000,
            position: "top-right",
          });
        }

        return { entity, data: response.data, success: true };
      }

      // Non-200/201 (unlikely here, but just in case)
      toast.error(translateBackendMessage(response.data?.message) || "Something went wrong");
      return rejectWithValue(response.data?.message || "Something went wrong");
    } catch (error) {
      const isOffline = !navigator.onLine;
      const isNetworkError =
        !error.response &&
        (error.message === "Network Error" || error.code === "ERR_NETWORK");

      // GET: try cache when offline or network error
      if ((isOffline || isNetworkError) && method === "GET") {
        const cachedData = await getApiResponse(fullUrl);
        if (cachedData) {
          // Offline-related toast hidden: cached data notification
          return { entity, data: cachedData, success: true, fromCache: true };
        }
      }

      // Mutation failed due to network: queue when offline. Same guard as
      // above — FormData and auth/payment requests are never queued; they fall
      // through to rejectWithValue below with the original network error.
      if (
        (isOffline || isNetworkError) &&
        ["POST", "PUT", "PATCH", "DELETE"].includes(method) &&
        isQueueableRequest({ url, data: requestData })
      ) {
        try {
          await queueRequest({
            entity,
            url,
            fullUrl,
            method,
            data: requestData,
            headers: {
              ...(localStorage.getItem("token")
                ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
                : {}),
              Accept: "application/json",
              "Accept-Language": i18n.language || "ar",
              ...headers,
            },
          });
          const queuedMsg =
            i18n.language === "ar"
              ? "تم حفظ البيانات محلياً وسيتم مزامنتها عند الاتصال"
              : "Data saved locally and will sync when online";
          // Offline-related toasts hidden per request
          // if (showSuccessToast !== false) { toast.info(queuedMsg, { autoClose: 4000, position: 'top-right' }); }
          const count = await getPendingCount();
          if (dispatch) dispatch(setPendingCount(count));
          return {
            entity,
            data: { message: queuedMsg, queued: true, success: true },
            success: true,
            fromQueue: true,
          };
        } catch (queueErr) {
          // Don't let a queueing failure hide the real network error — log both,
          // then fall through to rejectWithValue with the original error below.
          console.error(
            "[API Request] Failed to queue on network error (original error preserved):",
            { queueErr, originalError: error?.message || error?.code },
          );
        }
      }

      const status = error.response?.status;
      const responseData = error.response?.data || {};

      // Get errors object - prioritize errors field, then error/Error
      const backendError =
        responseData.errors || responseData.error || responseData.Error;

      // Translate at the single derivation point so every downstream toast
      // (lines below + showBackendErrors fallback) gets a human-readable string.
      const backendMessage = translateBackendMessage(responseData.message);
      const errorMessage =
        backendMessage || error.message || "An error occurred";

      // Show toast: for mutations (POST/PUT/DELETE) when online, always show backend error unless explicitly disabled
      const isMutation = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
      const shouldShowToast =
        showErrorToast !== false && (showErrorToast === true || isMutation);
      const normalizedBackendMessage =
        typeof backendMessage === "string" ? backendMessage.toLowerCase() : "";
      const normalizedErrorMessage =
        typeof errorMessage === "string" ? errorMessage.toLowerCase() : "";
      const isUnauthenticated =
        status === 401 &&
        (normalizedBackendMessage === "unauthenticated" ||
          normalizedBackendMessage === "unauthenticated." ||
          normalizedErrorMessage === "unauthenticated" ||
          normalizedErrorMessage === "unauthenticated." ||
          backendMessage === "غير مسجل دخول " ||
          errorMessage === "غير مسجل دخول ");

      if (shouldShowToast) {
        switch (status) {
          case 400:
          case 401:
          case 402:
          case 403:
          case 404:
          case 409:
          case 410:
          case 422:
            // Show backend errors if available, otherwise show error message
            if (backendError) {
              showBackendErrors(backendError, backendMessage || errorMessage);
            } else if (backendMessage) {
              toast.error(backendMessage, {
                autoClose: 5000,
                position: "top-right",
              });
            } else {
              toast.error(errorMessage, {
                autoClose: 5000,
                position: "top-right",
              });
            }
            break;
          case 500:
            toast.error(
              errorMessage || "Server Error: Please try again later",
              {
                autoClose: 5000,
                position: "top-right",
              },
            );
            break;
          default:
            toast.error(errorMessage, {
              autoClose: 5000,
              position: "top-right",
            });
        }
      } else {
      }

      // Exclude /pos/* from the global auto-logout. POS endpoints use a
      // different backend guard (employee_token) — a 401 there does NOT mean
      // the user's session is invalid for the rest of the app, so we must not
      // wipe storage and bounce to /login. Any genuinely-expired user token
      // will be detected by the next non-POS request.
      const isPosUrl = typeof url === "string" && url.startsWith("pos/");
      // Also exclude the two login endpoints. The login form deliberately
      // tries /auth/login first then falls back to /employees/auth/login;
      // a 401 on the first attempt is EXPECTED for employee credentials,
      // not a session expiry. Wiping storage + redirecting here would kill
      // the fallback attempt before it runs.
      const isAuthLoginUrl =
        url === "auth/login" || url === "employees/auth/login";

      if (isUnauthenticated && !isPosUrl && !isAuthLoginUrl && !skipAutoLogout) {
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch (storageError) {
          // ignore storage clearing issues
        }

        setTimeout(() => {
          window.location.href = "/login";
        }, 0);
      } else if (isUnauthenticated && skipAutoLogout) {
        // Caller opted out of the auto-logout behavior (e.g., a side-fetch
        // inside a POS flow). Surface the 401 as a console warning so it's
        // still visible during diagnosis, but don't wipe storage.
        // eslint-disable-next-line no-console
        console.warn("[401 skipAutoLogout]", {
          url: fullUrl,
          method,
          backendMessage,
        });
      } else if (isUnauthenticated && isPosUrl) {
        // Diagnostic so we can see in DevTools that POS auth is rejecting
        // the user token (likely needs employee_token instead).
        const tokenPreview = token
          ? `${token.slice(0, 12)}...${token.slice(-6)}`
          : "(none)";
        // eslint-disable-next-line no-console
        console.warn("[POS 401]", {
          url: fullUrl,
          method,
          tenant:
            headers?.["X-Tenant"] ||
            sessionStorage.getItem("tenant") ||
            "(none)",
          tokenPreview,
          backendMessage,
        });
      }

      return rejectWithValue({
        entity,
        error: errorMessage,
        status,
        backendError,
        backendMessage,
        success: false,
      });
    }
  },
);
